<template>
  <a-tooltip
    :title="errMsg ?? ''"
    :visible="errMsg !== null"
    placement="topLeft"
    overlay-class-name="flexible-input-error"
    :overlay-style="{ color: '#F00' }"
  >
    <editor-content
      class="flexible-input"
      :editor="editor"
      :spellcheck="false"
      style="display: inline"
    />
  </a-tooltip>
</template>

<script lang="ts" setup>
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, Node, useEditor } from '@tiptap/vue-3';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  class: {
    type: String,
    default: ''
  },
  validator: {
    type: Function as PropType<
      (value: string) => { valid: false; msg?: string } | { valid: true }
    >,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // 校验不通过时不显示提示
  quiet: {
    type: Boolean,
    default: false
  }
});

const errMsg = ref<string | null>(null);
const savedValue = ref('');

const handleSubmit = (value: string) => {
  // 提交时校验
  if (props.validator) {
    const res = props.validator(value);
    if (!res.valid) {
      if (props.quiet) {
        // 启用quiet时 直接恢复原有值
        editor.value?.commands.setContent(savedValue.value);
      } else {
        // 值不合法时 显示错误信息并重新聚焦到文本框
        errMsg.value = res.msg ?? '';
        editor.value?.setEditable(true);
        editor.value?.commands.focus();
        return;
      }
    }
  }
  // 无需校验或值合法时 隐藏报错信息并禁用可编辑 提交更新
  errMsg.value = null;
  editor.value?.setEditable(false);
  handleUpdate(editor.value?.getText() ?? '');
};

const handleUpdate = (value: string) => {
  emit('update:modelValue', value);
  emit('update', value);
};

const startEdit = () => {
  // 启用可编辑并保存当前值
  if (props.disabled || editor.value?.isEditable) return;
  editor.value?.setEditable(true);
  editor.value?.chain()?.focus().selectAll().run();
  savedValue.value = editor.value?.getText() ?? '';
};

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void;
  (event: 'update', value: string): void;
}>();

defineExpose({ startEdit });

const editor = useEditor({
  // 禁用换行和富文本格式
  extensions: [
    Node.create({
      name: 'doc',
      topNode: true,
      content: 'block'
    }),
    Text,
    Paragraph
  ],
  content: props.modelValue,
  editable: false,
  editorProps: {
    attributes: {
      // 绑定props中的class到编辑框
      class: props.class
    },
    handleDoubleClick() {
      startEdit();
    },
    handleKeyDown(view, event) {
      // 按下回车键时 尝试提交
      if (event.key === 'Enter') {
        handleSubmit(editor.value?.getText() ?? '');
        event.preventDefault();
        return true;
      }
      // 按下Escape键时 放弃编辑并恢复保存的值
      if (event.key === 'Escape') {
        errMsg.value = null;
        editor.value?.setEditable(false);
        editor.value?.commands.setContent(savedValue.value);
        handleUpdate(savedValue.value);
      }
    }
  },
  onBlur(e) {
    // 在编辑框外点击 失去焦点时 尝试提交
    if (e.editor.isEditable) handleSubmit(e.editor.getText());
  }
});
// v-model外部值变化时 手动更新编辑框内容
watch(
  () => props.modelValue,
  (newValue: string) =>
    newValue !== editor.value?.getText() &&
    editor.value?.commands.setContent(newValue)
);
</script>
<style lang="less">
.flexible-input {
  & * {
    display: inline-block;
    white-space: nowrap;
    margin: 0;
  }
  & > div {
    min-width: 8px;
  }
  & *::selection {
    color: #c2ddfb;
    background: #2559c9;
  }
}
.flexible-input-error .ant-tooltip-inner {
  color: #f00;
  font-weight: 500;
}
</style>
